/**
 * 매장 셀프 등록 폼.
 *
 * - 매장명(아이디)·이메일 중복 확인 후에만 저장 버튼이 열린다.
 * - 네이버 주소는 URL/단축링크/숫자 ID 어떤 형태든 받고 서버에서 최종 판별한다.
 * - 대표 사진은 관리자 플로우와 동일하게 긴 변 1600px webp 로 변환해 보낸다.
 */

import { busy } from './busyButton';
import initCodeInput from './codeInput';
import { createImagePicker } from './imagePicker';
import initScrollSpyNav from './scrollSpyNav';

const postAjax = async (root, action, data) => {
  // 임시: 화면 주소에 ?mode=test 가 있으면 서버도 Turnstile 테스트 키로 검증하게 함께 보낸다
  const mode =
    new URLSearchParams(window.location.search).get('mode') === 'test' ? { mode: 'test' } : {};
  const body = new URLSearchParams({ action, nonce: root.dataset.nonce, ...mode, ...data });
  const response = await fetch(root.dataset.ajaxUrl, { method: 'POST', body });
  const json = await response.json().catch(() => null);

  if (!json || !json.success) {
    const error = new Error(json?.data?.message || `요청 실패 (HTTP ${response.status})`);
    error.data = json?.data || {};
    throw error;
  }

  return json.data;
};

// Turnstile 토큰은 1회용: 서버 검증에 쓰고 나면 위젯을 리셋해 새 토큰을 받는다.
const turnstileToken = () => document.querySelector('[name="cf-turnstile-response"]')?.value || '';
const turnstileReset = () => {
  try {
    window.turnstile?.reset();
  } catch {
    // 위젯이 없으면 무시
  }
};

// 상태 메시지는 공통 메시지(.notice)에 담는다 — 글은 .notice__text 에, 색은 --success/--error/--warning 으로.
// 빈 메시지면 감춘다.
const setFieldStatus = (element, message, state) => {
  if (!element) {
    return;
  }

  const text = element.querySelector('.notice__text') || element;
  text.textContent = message;
  element.dataset.state = state || '';
  element.classList.remove('notice--success', 'notice--error', 'notice--warning');

  if (['success', 'error', 'warning'].includes(state)) {
    element.classList.add(`notice--${state}`);
  }

  element.hidden = '' === message;
};

const InitShopRegister = () => {
  const root = document.querySelector('[data-qrshop-register]');

  if (!root) {
    return;
  }

  const form = root.querySelector('form');
  const nameInput = root.querySelector('[data-register-name]');
  const nameCheckButton = root.querySelector('[data-register-name-check]');
  const nameStatus = root.querySelector('[data-register-name-status]');
  const urlPreview = root.querySelector('[data-register-url]');
  const emailInput = root.querySelector('[data-register-email]');
  const emailSendButton = root.querySelector('[data-register-email-send]');
  const emailStatus = root.querySelector('[data-register-email-status]');
  const emailPreview = root.querySelector('[data-register-email-preview]');
  const codeStep = root.querySelector('[data-register-code-step]');
  const codeInputEl = root.querySelector('[data-register-code]');
  const codeInput = initCodeInput(codeInputEl);
  const emailVerifyButton = root.querySelector('[data-register-email-verify]');
  const emailResendButton = root.querySelector('[data-register-email-resend]');
  const codeStatus = root.querySelector('[data-register-code-status]');
  const phoneInput = root.querySelector('[data-register-phone]');
  // 추천 코드(선택) — 캐시된 옛 템플릿에는 칸이 없을 수 있다(null 이면 전부 건너뛴다)
  const referralInput = root.querySelector('[data-register-referral]');
  const referralStatus = root.querySelector('[data-register-referral-status]');
  const termsInput = root.querySelector('[data-register-terms]');
  const privacyInput = root.querySelector('[data-register-privacy]');
  const marketingInput = root.querySelector('[data-register-marketing]');
  const promoInput = root.querySelector('[data-register-promo]');
  const consentAllInput = root.querySelector('[data-register-consent-all]');
  const consentInputs = [termsInput, privacyInput, marketingInput, promoInput].filter(Boolean);
  // 소식을 받을 매체(이메일 · 문자 · 앱 알림) — 소식 받기 항목과 함께 움직인다
  const marketingChannels = Array.from(root.querySelectorAll('[data-register-marketing-channel]'));
  const channelChecked = (key) =>
    marketingChannels.some(
      (input) => input.dataset.registerMarketingChannel === key && input.checked
    );
  const naverInput = root.querySelector('[data-register-naver]');
  const naverCheckButton = root.querySelector('[data-register-naver-check]');
  const naverStatus = root.querySelector('[data-register-naver-status]');
  const naverPreview = root.querySelector('[data-register-naver-preview]');
  const imageMount = root.querySelector('[data-register-image-mount]');
  const typeInputs = Array.from(root.querySelectorAll('[data-register-type]'));
  const kindInputs = Array.from(root.querySelectorAll('[data-register-kind]'));
  const submitButton = root.querySelector('[data-register-submit]');
  const submitHint = root.querySelector('[data-register-submit-hint]');
  const formStatus = root.querySelector('[data-register-status]');
  const donePanel = root.querySelector('[data-register-done]');
  const progressWrap = root.querySelector('[data-register-progress]');
  const progressBar = root.querySelector('[data-register-progress-bar]');
  const progressLabel = root.querySelector('[data-register-progress-label]');
  const progressPercent = root.querySelector('[data-register-progress-percent]');
  const progressText = root.querySelector('[data-register-progress-text]');

  // 숫자가 오래 안 바뀌는 구간(네이버 수집 · 매장 사이트 저장)에서도 멈춰 보이지 않도록,
  // creepTo 를 주면 1.5초마다 1%씩 creepTo 까지 저절로 올라간다. 단계 라벨은 그대로 둔다
  // (퍼센트는 aria-hidden 이라 이 움직임으로 스크린리더가 계속 읽지 않는다).
  let progressCreepTimer = 0;
  // 퍼센트 · 문구 span 이 없는 옛 캐시된 페이지와 새 JS 가 만나도 죽지 않도록 라벨 문구를 기억해 둔다
  let progressLabelText = '';

  const clearProgressCreep = () => {
    window.clearInterval(progressCreepTimer);
    progressCreepTimer = 0;
  };

  const applyProgressPercent = (value) => {
    const clamped = Math.min(100, Math.round(value));
    progressBar.style.width = `${clamped}%`;

    if (progressPercent) {
      progressPercent.textContent = `${clamped}%`;
    } else if (progressLabel) {
      // 옛 템플릿 호환 — 퍼센트 · 문구 span 이 없으면 라벨 통짜에 "NN% — 문구" 로 직접 채운다
      progressLabel.textContent = `${clamped}% — ${progressLabelText}`;
    }

    return clamped;
  };

  const setProgress = (percent, label, creepTo) => {
    clearProgressCreep();

    progressWrap.hidden = false;
    progressWrap.classList.toggle('is-active', percent < 100);

    progressLabelText = label;

    if (progressText) {
      progressText.textContent = label;
    }

    let current = applyProgressPercent(percent);

    if (creepTo && creepTo > current) {
      progressCreepTimer = window.setInterval(() => {
        if (current >= creepTo) {
          clearProgressCreep();
          return;
        }
        current += 1;
        applyProgressPercent(current);
      }, 1500);
    }
  };

  // 진행 막대를 감출 때는 항상 이 길로 — creep 인터벌도 같이 정리한다
  const hideProgress = () => {
    clearProgressCreep();
    progressWrap.hidden = true;
    progressWrap.classList.remove('is-active');
  };

  // 접수 처리 중 창을 닫으면 접수만 되고 이후 단계가 끊긴다 — [신청하기]를 누른 뒤부터
  // showDone 이나 1단계 실패로 폼이 다시 쓸 수 있게 될 때까지 붙였다 뗀다
  const handleBeforeUnload = (event) => {
    event.preventDefault();
    event.returnValue = '';
  };

  // 처리 중 다시 제출 막기 — refreshSubmitState 는 입력값만 보고 버튼을 켜므로, 처리 중에
  // (재확인·인증 같은) input 이벤트가 한 번이라도 지나가면 버튼이 다시 열려 이중 제출될 수 있었다.
  let submitting = false;

  // 제출 버튼 위 입력 영역(카드 · 캡차)만 잠근다 — register__submit(버튼 · 진행 막대)는 계속 보이고 읽혀야 한다
  const formLockTargets = Array.from(form.children).filter(
    (child) => !child.classList.contains('register__submit')
  );

  const lockForm = () => {
    formLockTargets.forEach((element) => {
      element.inert = true;
    });
  };

  const unlockForm = () => {
    formLockTargets.forEach((element) => {
      element.inert = false;
    });
  };

  // 좌측 퀵 네비 (모바일: 상단 칩)
  const navEl = root.querySelector('[data-register-nav]');
  if (navEl) {
    initScrollSpyNav(
      navEl,
      Array.from(root.querySelectorAll('[data-nav-label]')).map((section) => ({
        el: section,
        label: section.dataset.navLabel,
      }))
    );
  }

  // 완료 화면 — 제목·리드·폼을 모두 걷어 내고 화면 가운데에 그림과 안내만 남긴다.
  // mail    : 매장 사이트가 안내 메일을 큐에 넣었다 → "메일이 안 왔나요?" 를 조용히 덧붙인다
  // nomail  : 매장은 만들어졌지만 메일 발송이 확인되지 않았다 → 접속 방법을 바로 보여 준다
  // pending : 접수 뒤 단계가 실패했다 → 관리자가 마무리하므로 접속 안내는 감춘다
  const showDone = ({ state, shopName, email, shopUrl, warning }) => {
    root.querySelectorAll('[data-register-intro]').forEach((element) => {
      element.hidden = true;
    });
    const layout = root.querySelector('[data-register-layout]');
    if (layout) {
      layout.hidden = true;
    }
    form.hidden = true;
    hideProgress();
    window.removeEventListener('beforeunload', handleBeforeUnload);
    // 폼은 위에서 시작하지만 완료 화면은 헤더와 푸터 사이 한가운데에 둔다
    root.closest('.site-main')?.classList.add('site-main--center');

    donePanel.dataset.registerDoneState = state;
    donePanel.querySelector('[data-register-done-title]').textContent =
      'pending' === state ? '접수가 완료되었습니다.' : '세팅이 완료되었습니다.';
    donePanel.querySelectorAll('[data-register-done-name]').forEach((element) => {
      element.textContent = shopName;
    });
    donePanel.querySelectorAll('[data-register-done-email]').forEach((element) => {
      element.textContent = email;
    });
    donePanel.querySelectorAll('[data-register-done-text]').forEach((element) => {
      element.hidden = element.dataset.registerDoneText !== state;
    });

    const help = donePanel.querySelector('[data-register-done-help]');
    if (help) {
      help.hidden = 'pending' === state;
      help.querySelector('[data-register-done-help-title]').textContent =
        'nomail' === state ? '이렇게 접속해 주세요' : '안내 메일이 오지 않았나요?';
      help.querySelector('[data-register-done-step-spam]').hidden = 'nomail' === state;

      const shopLink = help.querySelector('[data-register-done-shop-url]');
      shopLink.href = shopUrl || '#';
      shopLink.textContent = shopUrl.replace(/^https?:\/\//, '');
      shopLink.parentElement.hidden = '' === shopUrl;
    }

    const warningElement = donePanel.querySelector('[data-register-done-warning]');
    if (warning && warningElement) {
      setFieldStatus(warningElement, warning, 'warning');
    }

    donePanel.hidden = false;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 관리자 미리보기 — /register/?mode=test&simulate=done|done-nomail|done-pending 이면
  // 폼을 거치지 않고 그 상태의 완료 화면을 바로 그린다 (값은 PHP 가 관리자일 때만 넘긴다).
  const simulateDone = /^done(?:-(nomail|pending))?$/.exec(root.dataset.simulate || '');
  if (simulateDone) {
    const siteUrl = typeInputs[0]?.dataset.siteUrl || '';
    const state = simulateDone[1] || 'mail';

    showDone({
      state,
      shopName: 'qrshop',
      email: 'owner@example.com',
      shopUrl: siteUrl ? `${siteUrl}/qrshop/` : '',
      warning:
        'pending' === state
          ? '일부 처리가 완료되지 못했습니다 (사이트 저장에 실패했습니다: HTTP 500) — 접수는 정상 완료되었고, 관리자가 확인 후 마무리합니다.'
          : '',
    });
  }

  // 고른 매장 형태 (라디오). 아무것도 안 골랐으면 첫 항목으로 본다.
  const shopType = () =>
    (typeInputs.find((input) => input.checked) || typeInputs[0])?.value || 'salon';

  // 고른 운영 형태 (라디오). 접수 요청에만 싣는다 — 중복 확인 · 이메일 인증과는 상관없다.
  const shopKind = () =>
    (kindInputs.find((input) => input.checked) || kindInputs[0])?.value || 'shop';

  // 확인된 값 되비추기(.register__preview) — 앞머리는 흐리게, 값은 진하게. 값이 비면 감춘다
  const setPreview = (element, base, value) => {
    if (!element) {
      return;
    }

    element.textContent = '';

    if (value) {
      const baseSpan = document.createElement('span');
      baseSpan.className = 'register__preview-base';
      baseSpan.textContent = base;

      const valueSpan = document.createElement('span');
      valueSpan.className = 'register__preview-value';
      valueSpan.textContent = value;

      element.append(baseSpan, valueSpan);
    }

    element.hidden = !value;
  };

  // 매장 페이지 주소 미리보기 — 아이디가 있을 때만 "사이트/아이디" 를 보여 준다
  const refreshUrlPreview = () => {
    const typeInput = typeInputs.find((input) => input.checked) || typeInputs[0];
    const siteUrl = typeInput?.dataset.siteUrl || '';

    setPreview(urlPreview, `${siteUrl}/`, nameInput.value.trim());
  };

  // 중복 확인을 통과한 매장명 · 인증 코드를 맞힌 이메일. 입력이 바뀌면 다시 확인해야 한다.
  const verified = { name: '', email: '' };

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailValue = () => emailInput.value.trim().toLowerCase();
  const emailLooksValid = () => EMAIL_PATTERN.test(emailValue());

  // 필수 동의(이용 약관 · 개인정보 수집 · 이용). 체크박스가 없는 화면이면 동의한 것으로 본다
  const termsAgreed = () => !termsInput || termsInput.checked;
  const privacyAgreed = () => !privacyInput || privacyInput.checked;

  // 아직 켜지 않은 필수 동의 — [체크박스, 안내 문구]. 모두 켰으면 null
  const missingConsent = () => {
    if (!termsAgreed()) {
      return [termsInput, '이용 약관에 동의해 주세요.'];
    }
    if (!privacyAgreed()) {
      return [privacyInput, '개인정보 수집 · 이용에 동의해 주세요.'];
    }
    return null;
  };

  // 신청 버튼은 필수 정보(매장명 중복 확인 · 네이버 주소 · 이메일 · 휴대전화 · 필수 동의)가 다 채워져야 열린다
  const refreshSubmitState = () => {
    const nameVerified =
      verified.name !== '' && verified.name === nameInput.value.trim().toLowerCase();
    const emailVerified = verified.email !== '' && verified.email === emailValue();
    const filled = naverInput.value.trim() !== '' && phoneInput.value.length >= 10;

    // 처리 중에는 값이 다시 유효해 보여도 버튼을 계속 잠근다
    submitButton.disabled =
      submitting || !(nameVerified && emailVerified && filled && !missingConsent());

    // 중복 확인을 통과한 매장명이면 주소 미리보기를 통과 색으로 바꾼다
    urlPreview?.classList.toggle('register__preview--ok', nameVerified);

    // 신청할 수 있게 되면 "다 채우면 신청할 수 있다"는 안내는 치운다
    if (submitHint) {
      submitHint.hidden = !submitButton.disabled;
    }
  };

  const runCheck = async (kind, input, button, statusElement) => {
    const value = input.value.trim();

    if (!value) {
      setFieldStatus(statusElement, '매장명을 입력해 주세요.', 'error');
      return;
    }

    const release = busy(button);
    setFieldStatus(statusElement, '확인 중...', 'info');

    try {
      const result = await postAjax(root, 'qrshop_register_check', {
        kind,
        value,
        shop_type: shopType(),
      });
      verified[kind] = result.normalized;
      input.value = result.normalized;
      refreshUrlPreview();
      setFieldStatus(statusElement, result.message, 'success');
    } catch (error) {
      verified[kind] = '';
      setFieldStatus(statusElement, error.message, 'error');
    } finally {
      release();
      refreshSubmitState();
    }
  };

  nameCheckButton.addEventListener('click', () =>
    runCheck('name', nameInput, nameCheckButton, nameStatus)
  );

  nameInput.addEventListener('input', () => {
    nameInput.value = nameInput.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
    refreshUrlPreview();

    if (nameInput.value.trim().toLowerCase() !== verified.name) {
      verified.name = '';
      setFieldStatus(nameStatus, '중복 확인이 필요합니다.', '');
      refreshSubmitState();
    }
  });

  // 매장명은 매장 형태(대상 사이트)마다 따로 검사하므로, 형태를 바꾸면 다시 확인해야 한다
  typeInputs.forEach((input) => {
    input.addEventListener('change', () => {
      refreshUrlPreview();

      if ('' !== verified.name) {
        verified.name = '';
        setFieldStatus(nameStatus, '매장 형태가 바뀌었습니다. 중복 확인을 다시 해 주세요.', '');
        refreshSubmitState();
      }

      // 네이버 매장 중복도 대상 사이트마다 다르므로 확인 결과를 걷어 낸다
      if (!naverPreview.hidden) {
        setPreview(naverPreview, '', '');
        setFieldStatus(naverStatus, '매장 형태가 바뀌었습니다. 매장 확인을 다시 해 주세요.', '');
      }
    });
  });

  naverCheckButton.addEventListener('click', async () => {
    const value = naverInput.value.trim();

    if (!value) {
      setFieldStatus(naverStatus, '네이버 주소를 입력해 주세요.', 'error');
      return;
    }

    const release = busy(naverCheckButton);
    setFieldStatus(naverStatus, '네이버에서 매장 정보를 확인하는 중...', 'info');

    try {
      const result = await postAjax(root, 'qrshop_register_naver_check', {
        naver: value,
        // 같은 네이버 매장이 이미 있는지는 매장 형태(대상 사이트)마다 따로 본다
        shop_type: shopType(),
        turnstile: turnstileToken(),
        simulate: root.dataset.simulate || '',
      });
      // 매장명을 찾았으면 아이디 아래 주소처럼 상자(통과 색)에 되비추고, 못 찾았을 때만 메시지로 남긴다
      if (result.shop_title) {
        setFieldStatus(naverStatus, '', '');
        setPreview(naverPreview, '확인된 매장 ', result.shop_title);
      } else {
        setPreview(naverPreview, '', '');
        setFieldStatus(
          naverStatus,
          '매장은 확인했지만 매장명을 읽지 못했습니다. 주소를 다시 확인해 주세요.',
          'warning'
        );
      }
    } catch (error) {
      setPreview(naverPreview, '', '');
      setFieldStatus(naverStatus, error.message, 'error');
    } finally {
      turnstileReset();
      release();
    }
  });

  naverInput.addEventListener('input', () => {
    setFieldStatus(naverStatus, '', '');
    setPreview(naverPreview, '', '');
    refreshSubmitState();
  });

  // ── 이메일 인증 ──
  // 형식이 어긋나면 칸을 벗어날 때 바로 알려 준다 (가벼운 검사). 실제 수신 여부는 인증 코드로 확인한다.
  const showEmailFormatError = () => {
    if (emailInput.value.trim() !== '' && !emailLooksValid()) {
      setFieldStatus(
        emailStatus,
        '올바른 이메일 주소 형식이 아닙니다. 예: owner@example.com',
        'error'
      );
      return true;
    }

    return false;
  };

  emailInput.addEventListener('blur', showEmailFormatError);

  emailInput.addEventListener('input', () => {
    // 인증한 주소와 달라지면 인증을 무효로 돌린다
    if (verified.email !== '' && verified.email !== emailValue()) {
      verified.email = '';
      setPreview(emailPreview, '', '');
      setFieldStatus(emailStatus, '이메일이 바뀌었습니다. 인증 코드를 다시 요청해 주세요.', '');
    } else if (emailStatus?.dataset.state === 'error' && emailLooksValid()) {
      setFieldStatus(emailStatus, '', '');
    }

    codeStep.hidden = true;
    refreshSubmitState();
  });

  // 재요청 대기 — 매장 사이트가 알려 준 시간 동안 버튼을 잠그고 남은 초를 보여 준다
  let resendTimer = 0;

  const lockResend = (seconds) => {
    let left = Math.max(0, Math.round(seconds));
    const label = emailSendButton.textContent;

    window.clearInterval(resendTimer);
    emailSendButton.disabled = true;
    emailResendButton.disabled = true;

    const tick = () => {
      if (left <= 0) {
        window.clearInterval(resendTimer);
        emailSendButton.disabled = false;
        emailResendButton.disabled = false;
        emailSendButton.textContent = label;
        return;
      }

      emailSendButton.textContent = `${label} (${left}초)`;
      left -= 1;
    };

    tick();
    resendTimer = window.setInterval(tick, 1000);
  };

  const requestEmailCode = async (triggerButton) => {
    if (emailInput.value.trim() === '') {
      setFieldStatus(emailStatus, '이메일 주소를 입력해 주세요.', 'error');
      emailInput.focus();
      return;
    }

    if (showEmailFormatError()) {
      emailInput.focus();
      return;
    }

    // 같이 막히는 옆 버튼(재전송/요청)은 disabled 만, 도는 원은 실제로 누른 버튼에만
    const otherButton = triggerButton === emailSendButton ? emailResendButton : emailSendButton;
    const release = busy(triggerButton);
    otherButton.disabled = true;
    setFieldStatus(codeStatus, '', '');
    setFieldStatus(emailStatus, '인증 코드를 보내는 중...', 'info');

    try {
      const result = await postAjax(root, 'qrshop_register_email_request', {
        email: emailValue(),
        shop_type: shopType(),
        turnstile: turnstileToken(),
      });
      setFieldStatus(emailStatus, result.message, 'success');
      codeStep.hidden = false;
      codeInput.clear();
      codeInput.focus();
      // 도는 원만 거두고, disabled 는 재전송 대기(lockResend)가 이어서 관리한다
      release();
      lockResend(result.resend_after || 60);
    } catch (error) {
      setFieldStatus(emailStatus, error.message, 'error');
      release();
      otherButton.disabled = false;
    } finally {
      turnstileReset();
    }
  };

  emailSendButton.addEventListener('click', () => requestEmailCode(emailSendButton));
  emailResendButton.addEventListener('click', () => requestEmailCode(emailResendButton));

  const verifyEmailCode = async () => {
    // 클릭과 코드 칸 Enter(keydown) 이 겹쳐 다시 불릴 수 있다 — 진행 중이면 무시
    if ('true' === emailVerifyButton.getAttribute('aria-busy')) {
      return;
    }

    const code = codeInput.value();

    if (code.length !== 6) {
      setFieldStatus(codeStatus, '메일로 받은 6자리 숫자를 입력해 주세요.', 'error');
      codeInput.focus();
      return;
    }

    const release = busy(emailVerifyButton, '확인 중…');
    setFieldStatus(codeStatus, '확인 중...', 'info');

    try {
      const result = await postAjax(root, 'qrshop_register_email_verify', {
        email: emailValue(),
        code,
        shop_type: shopType(),
      });
      verified.email = (result.email || emailValue()).toLowerCase();
      emailInput.value = verified.email;
      codeStep.hidden = true;
      setFieldStatus(codeStatus, '', '');
      setFieldStatus(emailStatus, '', '');
      setPreview(emailPreview, '인증된 이메일 ', verified.email);
      window.clearInterval(resendTimer);
      emailSendButton.disabled = false;
      emailSendButton.textContent = '인증코드 요청';
    } catch (error) {
      setFieldStatus(codeStatus, error.message, 'error');
    } finally {
      release();
      refreshSubmitState();
    }
  };

  emailVerifyButton.addEventListener('click', verifyEmailCode);

  // 칸 안 input · keydown 은 감싸는 요소로 올라온다 (codeInput.js)
  codeInputEl.addEventListener('input', () => {
    setFieldStatus(codeStatus, '', '');
  });

  // 코드 칸에서 엔터는 폼 제출이 아니라 코드 확인이다
  codeInputEl.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      verifyEmailCode();
    }
  });

  phoneInput.addEventListener('input', () => {
    phoneInput.value = phoneInput.value.replace(/\D/g, '').slice(0, 11);
    refreshSubmitState();
  });

  // ── 추천 코드 ──
  // 서버(Referral::normalize)와 같은 정리 — 대문자 · 공백 제거. 모양 검사는 서버가 한다.
  const REFERRAL_MAX = 16;
  const normalizeReferral = (value) =>
    String(value || '')
      .toUpperCase()
      .replace(/\s+/g, '')
      .slice(0, REFERRAL_MAX);
  const referralValue = () => (referralInput ? normalizeReferral(referralInput.value) : '');

  // 마지막 확인 결과 — 어떤 값을 확인했고 통과했는지. 값이 바뀌면 무효
  const referralChecked = { code: '', ok: false };
  let referralTimer = 0;
  let referralSeq = 0;

  // 입력이 멈추면 쓸 수 있는지만 묻는다. 응답은 "쓸 수 있다 / 확인되지 않는다" 뿐이고, 늦게 온 옛 응답은 버린다
  const checkReferral = async () => {
    const code = referralValue();

    if (!code) {
      setFieldStatus(referralStatus, '', '');
      return;
    }

    const seq = (referralSeq += 1);
    setFieldStatus(referralStatus, '코드를 확인하는 중...', 'info');

    try {
      const result = await postAjax(root, 'qrshop_register_referral_check', { code });

      if (seq !== referralSeq || referralValue() !== code) {
        return;
      }

      referralChecked.code = result.code || code;
      referralChecked.ok = true;
      setFieldStatus(referralStatus, result.message, 'success');
    } catch (error) {
      if (seq !== referralSeq || referralValue() !== code) {
        return;
      }

      // 코드 자체가 틀린 것(invalid)만 기억한다. 요청 제한 · 연결 오류는 기록하지 않고 서버 재검증에 맡긴다
      referralChecked.code = error.data?.invalid ? code : '';
      referralChecked.ok = false;
      setFieldStatus(referralStatus, error.message, 'error');
    }
  };

  if (referralInput) {
    referralInput.addEventListener('input', () => {
      const normalized = normalizeReferral(referralInput.value);

      if (referralInput.value !== normalized) {
        referralInput.value = normalized;
      }

      window.clearTimeout(referralTimer);
      referralSeq += 1;
      setFieldStatus(referralStatus, '', '');

      if (normalized) {
        referralTimer = window.setTimeout(checkReferral, 600);
      }
    });

    // ?ref=CODE 로 들어온 손님 — 정리한 값을 채우고 바로 확인한다
    const refParam = normalizeReferral(new URLSearchParams(window.location.search).get('ref'));

    if (refParam && !referralInput.value) {
      referralInput.value = refParam;
      checkReferral();
    }
  }

  // 전체 동의 — 누르면 필수 · 선택 항목(매체까지)이 모두 켜지고, 항목 · 매체 하나라도 풀리면 전체 동의도 풀린다
  const syncConsentAll = () => {
    if (consentAllInput) {
      consentAllInput.checked =
        consentInputs.length > 0 &&
        consentInputs.every((input) => input.checked) &&
        marketingChannels.every((input) => input.checked);
    }
  };

  // 소식 받기 항목 ↔ 매체: 항목을 켜면 매체 셋이 다 켜지고 끄면 다 꺼진다. 매체는 하나씩 끌 수 있고,
  // 하나라도 켜져 있으면 항목이 켜지며 모두 꺼지면 항목도 꺼진다.
  const setChannels = (checked) => {
    marketingChannels.forEach((input) => {
      input.checked = checked;
    });
  };

  const syncMarketingFromChannels = () => {
    if (marketingInput && marketingChannels.length > 0) {
      marketingInput.checked = marketingChannels.some((input) => input.checked);
    }
  };

  consentAllInput?.addEventListener('change', () => {
    consentInputs.forEach((input) => {
      input.checked = consentAllInput.checked;
    });
    setChannels(consentAllInput.checked);
    refreshSubmitState();
  });

  consentInputs.forEach((input) => {
    input.addEventListener('change', () => {
      if (input === marketingInput) {
        setChannels(input.checked);
      }
      syncConsentAll();
      refreshSubmitState();
    });
  });

  marketingChannels.forEach((input) => {
    input.addEventListener('change', () => {
      syncMarketingFromChannels();
      syncConsentAll();
      refreshSubmitState();
    });
  });

  // 대표 사진: 공용 이미지 선택 컴포넌트 (선택 즉시 webp 변환까지 완료)
  let shopImageDataUrl = '';

  if (imageMount) {
    const picker = createImagePicker({
      initialUrl: '',
      onStaged: (dataUrl) => {
        shopImageDataUrl = dataUrl || '';
      },
      onError: (message) => setFieldStatus(formStatus, message, 'error'),
    });
    imageMount.append(picker.element);
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    refreshSubmitState();

    if (submitButton.disabled) {
      const consent = missingConsent();
      setFieldStatus(
        formStatus,
        consent ? consent[1] : '매장명 중복 확인과 이메일 인증을 먼저 완료해 주세요.',
        'error'
      );
      consent?.[0].focus();
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value.trim())) {
      setFieldStatus(formStatus, '올바른 이메일 주소를 입력해 주세요.', 'error');
      emailInput.focus();
      return;
    }

    if (!naverInput.value.trim()) {
      setFieldStatus(formStatus, '네이버 주소를 입력해 주세요.', 'error');
      naverInput.focus();
      return;
    }

    if (phoneInput.value.length < 10) {
      setFieldStatus(formStatus, '휴대전화 번호를 숫자만으로 정확히 입력해 주세요.', 'error');
      phoneInput.focus();
      return;
    }

    const consent = missingConsent();
    if (consent) {
      setFieldStatus(formStatus, consent[1], 'error');
      consent[0].focus();
      return;
    }

    // 추천 코드는 비어 있어도 되지만, 방금 확인해서 틀린 것으로 나온 값은 보내기 전에 막는다 (서버도 다시 본다)
    const referralCode = referralValue();

    if (referralCode && referralChecked.code === referralCode && !referralChecked.ok) {
      setFieldStatus(
        formStatus,
        '추천 코드가 확인되지 않습니다. 코드를 고치거나 비운 뒤 다시 신청해 주세요.',
        'error'
      );
      referralInput.focus();
      return;
    }

    submitting = true;
    lockForm();
    const releaseSubmit = busy(submitButton, '신청 처리 중…');
    setFieldStatus(formStatus, '', '');
    window.addEventListener('beforeunload', handleBeforeUnload);

    let submitted = null;

    // 1단계: 접수 저장. 여기서 실패하면 처음부터 다시 시도할 수 있다.
    try {
      const imageDataUrl = shopImageDataUrl;

      setProgress(8, '접수 정보를 저장하는 중입니다...');

      submitted = await postAjax(root, 'qrshop_register_submit', {
        shop_type: shopType(),
        shop_kind: shopKind(),
        shop_name: nameInput.value.trim(),
        email: emailInput.value.trim(),
        phone: phoneInput.value,
        naver: naverInput.value.trim(),
        image: imageDataUrl,
        terms_consent: termsAgreed() ? '1' : '',
        privacy_consent: privacyAgreed() ? '1' : '',
        // 소식 받기 — 매체별 값과, 옛 서버(매체를 모르는 버전)를 위한 "하나라도" 값을 함께 보낸다
        marketing_consent: marketingInput?.checked ? '1' : '',
        marketing_email: channelChecked('email') ? '1' : '',
        marketing_sms: channelChecked('sms') ? '1' : '',
        marketing_push: channelChecked('push') ? '1' : '',
        promo_consent: promoInput?.checked ? '1' : '',
        // 추천 코드(선택) — 빈 값이면 서버가 코드 없음으로 본다
        referral_code: referralCode,
        hp: root.querySelector('[data-register-hp]')?.value || '',
        turnstile: turnstileToken(),
      });
    } catch (error) {
      turnstileReset();
      hideProgress();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      setFieldStatus(formStatus, error.message, 'error');

      // 서버가 추천 코드 때문에 거절했으면 그 칸에도 표시하고 포커스를 준다
      if (referralInput && referralCode && error.data?.field === 'referral') {
        referralChecked.code = referralCode;
        referralChecked.ok = false;
        setFieldStatus(referralStatus, '확인되지 않는 코드입니다.', 'error');
      }
      submitting = false;
      unlockForm();
      releaseSubmit();
      // 입력칸에서 Enter 로 제출했다면 잠글 때 포커스가 body 로 빠졌다 — 다시 누를 버튼으로 돌려 준다
      submitButton.focus();
      return;
    }

    // 2단계~: 네이버 수집 → 가격표 이미지 변환 → 매장 사이트 저장.
    // 접수는 이미 끝났으므로 이후 실패해도 폼으로 되돌리지 않고 안내만 남긴다.
    const job = { post_id: submitted.post_id, job_key: submitted.job_key };
    let warning = '';
    let pushed = null;

    try {
      setProgress(15, '네이버 매장 정보를 수집하는 중입니다...', 24);
      const scraped = await postAjax(root, 'qrshop_register_scrape', job);

      const total = scraped.image_total || 0;
      let offset = 0;

      while (offset < (total || 0)) {
        setProgress(
          25 + (total ? (offset / total) * 55 : 0),
          `가격표 이미지를 변환하는 중입니다... (${Math.min(offset + 1, total)}/${total})`
        );
        const step = await postAjax(root, 'qrshop_register_images', { ...job, offset });
        offset = step.processed;

        if (step.done) {
          break;
        }
      }

      setProgress(85, '매장 사이트에 저장하는 중입니다...', 97);
      pushed = await postAjax(root, 'qrshop_register_push', job);
      setProgress(100, '저장이 완료되었습니다.');
    } catch (error) {
      warning = `일부 처리가 완료되지 못했습니다 (${error.message}) — 접수는 정상 완료되었고, 관리자가 확인 후 마무리합니다.`;
    }

    const siteUrl =
      (typeInputs.find((input) => input.checked) || typeInputs[0])?.dataset.siteUrl || '';
    const shopName = nameInput.value.trim();

    showDone({
      state: warning ? 'pending' : pushed?.mail_queued ? 'mail' : 'nomail',
      shopName,
      email: emailInput.value.trim(),
      shopUrl: pushed?.shop_url || (siteUrl ? `${siteUrl}/${shopName}/` : ''),
      warning,
    });
  });
};

export default InitShopRegister;
